import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import './Teachers.css';

const Teachers = () => {
    const { t, i18n } = useTranslation();
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedTeacherId, setSelectedTeacherId] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        fullNameAr: '',
        fullNameEn: ''
    });
    const [formError, setFormError] = useState('');
    const [formLoading, setFormLoading] = useState(false);

    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        try {
            const response = await api.get('/admin/teachers');
            if (response.data.success) {
                setTeachers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching teachers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (teacher = null) => {
        setShowPassword(false);
        if (teacher) {
            setIsEditing(true);
            setSelectedTeacherId(teacher.user_id);
            setFormData({
                username: teacher.username,
                email: teacher.email,
                password: '',
                fullNameAr: teacher.full_name_ar,
                fullNameEn: teacher.full_name_en
            });
        } else {
            setIsEditing(false);
            setSelectedTeacherId(null);
            setFormData({ username: '', email: '', password: '', fullNameAr: '', fullNameEn: '' });
        }
        setFormError('');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormLoading(true);

        try {
            let response;
            if (isEditing) {
                response = await api.put(`/admin/teachers/${selectedTeacherId}`, formData);
            } else {
                response = await api.post('/admin/teachers', formData);
            }

            if (response.data.success) {
                fetchTeachers();
                setShowModal(false);
            }
        } catch (error) {
            console.error('Teacher Action Error:', error.response?.data);
            setFormError(error.response?.data?.message || t('common.error'));
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('admin.teachers.deleteConfirm'))) return;

        try {
            const response = await api.delete(`/admin/teachers/${id}`);
            if (response.data.success) {
                fetchTeachers();
            }
        } catch (error) {
            alert(error.response?.data?.message || t('common.error'));
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const isAr = i18n.language === 'ar';

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t('common.loading')}</p>
            </div>
        );
    }

    return (
        <div className="teachers-page fade-in">
            <div className="page-header">
                <h1>{t('admin.teachers.title')}</h1>
                <div className="header-actions no-print">
                    <button className="btn btn-outline" onClick={handlePrint}>
                        <span className="material-icons">print</span> {t('common.print') || 'Print'}
                    </button>
                    <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                        <span className="material-icons">add</span> {t('admin.teachers.addTeacher')}
                    </button>
                </div>
            </div>

            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{t('common.name')}</th>
                            <th>{t('common.email')}</th>
                            <th>{t('common.courses')}</th>
                            <th>{t('common.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {teachers.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                                    {t('common.noData')}
                                </td>
                            </tr>
                        ) : (
                            teachers.map((teacher, index) => (
                                <tr key={teacher.user_id}>
                                    <td>{index + 1}</td>
                                    <td>{isAr ? teacher.full_name_ar : teacher.full_name_en}</td>
                                    <td>{teacher.email}</td>
                                    <td className="courses-list-cell">
                                        {isAr ? (teacher.courses_ar || '-') : (teacher.courses_en || '-')}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="btn btn-edit btn-sm"
                                                onClick={() => handleOpenModal(teacher)}
                                            >
                                                <span className="material-icons">edit</span>
                                                {t('common.edit')}
                                            </button>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => handleDelete(teacher.user_id)}
                                            >
                                                <span className="material-icons">delete</span>
                                                {t('common.delete')}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isEditing ? t('admin.teachers.editTeacher') : t('admin.teachers.addTeacher')}</h2>
                        </div>

                        <form onSubmit={handleSubmit} className="teacher-form">
                            {formError && (
                                <div className="alert alert-danger">
                                    <span className="material-icons">error_outline</span> {formError}
                                </div>
                            )}

                            <div className="form-row">
                                <div className="form-group flex-1">
                                    <label className="label">{t('login.username')}</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={formData.username}
                                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group flex-1">
                                    <label className="label">{t('common.email')}</label>
                                    <input
                                        type="email"
                                        className="input"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group flex-1">
                                    <label className="label">{t('admin.teachers.teacherNameAr')}</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={formData.fullNameAr}
                                        onChange={(e) => setFormData({ ...formData, fullNameAr: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group flex-1">
                                    <label className="label">{t('admin.teachers.teacherNameEn')}</label>
                                    <input
                                        type="text"
                                        className="input"
                                        value={formData.fullNameEn}
                                        onChange={(e) => setFormData({ ...formData, fullNameEn: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="label">
                                    {t('common.password')} {isEditing && <small>{t('common.optional')}</small>}
                                </label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        className="input"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required={!isEditing}
                                        minLength={6}
                                        placeholder={isEditing ? '••••••••' : ''}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-icon"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <span className="material-icons">
                                            {showPassword ? 'visibility_off' : 'visibility'}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                                    {formLoading ? t('common.loading') : t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Teachers;
